import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Truck } from 'lucide-react';

const AssignTruckModal = ({
  isOpen,
  onClose,
  selectedDispatch,
  assignData,
  setAssignData,
  companies,
  filteredTrucks,
  filteredTrailers,
  filteredDrivers,
  businesses,
  compras,
  onSubmit
}) => {
  // Verificar que assignData tenga valores válidos
  const companyId = assignData?.company_id || '';
  const truckId = assignData?.truck_id || '';
  const trailerId = assignData?.trailer_id || '';
  const driverId = assignData?.driver_id || '';
  const negocioId = assignData?.negocio_id || '';
  const compraId = assignData?.compra_id || '';
  
  const handleCompanyChange = (value) => {
    setAssignData(prev => ({ 
      ...prev,
      company_id: value, 
      truck_id: '', 
      trailer_id: '', 
      driver_id: '' 
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Asignar Camión al Despacho {selectedDispatch?.dispatch_number}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-4">
            {/* Selectores de Negocio y Compra en grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Negocio (Descarga) <span className="text-slate-400 text-[10px]">(opc.)</span></Label>
                <Select
                  value={negocioId || "none"}
                  onValueChange={(v) => setAssignData({ ...assignData, negocio_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Sin negocio" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin negocio</SelectItem>
                    {(businesses || []).filter(b => b.status === 'activo').map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        <span className="font-mono font-semibold">{b.business_number}</span>
                        {' · '}{b.client_name || '—'}
                        {' · '}{b.producto}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Compra (Carga) <span className="text-slate-400 text-[10px]">(opc.)</span></Label>
                <Select
                  value={compraId || "none"}
                  onValueChange={(v) => setAssignData({ ...assignData, compra_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Sin compra" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin compra</SelectItem>
                    {(compras || []).filter(c => (c.kg_disponibles || c.cantidad_kg) > 0).map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="font-mono font-semibold">{c.tipo_grano}</span>
                        {' · '}{c.proveedor_name || '—'}
                        {' · Disp: '}{((c.kg_disponibles ?? c.cantidad_kg) / 1000).toFixed(1)}T
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Empresa Transportista *</Label>
              <Select 
                value={companyId} 
                onValueChange={handleCompanyChange}
              >
                <SelectTrigger><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.alias || c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Patente (Camión) *</Label>
              <Select 
                value={truckId} 
                onValueChange={(v) => setAssignData({ ...assignData, truck_id: v })} 
                disabled={!companyId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={companyId ? "Seleccionar patente" : "Primero seleccione empresa"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredTrucks.length > 0 ? (
                    filteredTrucks.map(t => (<SelectItem key={t.id} value={t.id}>{t.license_plate} - {t.brand}</SelectItem>))
                  ) : (
                    <SelectItem value="no_trucks" disabled>No hay camiones disponibles</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Patente (Remolque)</Label>
              <Select 
                value={trailerId || "none"} 
                onValueChange={(v) => setAssignData({ ...assignData, trailer_id: v === "none" ? "" : v })} 
                disabled={!companyId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={companyId ? "Seleccionar (opcional)" : "Primero seleccione empresa"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin remolque</SelectItem>
                  {filteredTrailers.map(t => (<SelectItem key={t.id} value={t.id}>{t.license_plate}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Chofer *</Label>
              <Select 
                value={driverId} 
                onValueChange={(v) => setAssignData({ ...assignData, driver_id: v })} 
                disabled={!companyId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={companyId ? "Seleccionar chofer" : "Primero seleccione empresa"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredDrivers.length > 0 ? (
                    filteredDrivers.map(d => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))
                  ) : (
                    <SelectItem value="no_drivers" disabled>No hay choferes disponibles</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button 
            type="submit" 
            className="w-full bg-green-600 hover:bg-green-700 text-white" 
            disabled={!companyId || !truckId || !driverId}
          >
            <Truck size={16} className="mr-2" /> Asignar Camión
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssignTruckModal;
